import type { ComputedRef } from 'vue'
import type { CustomerStageComment } from '@entities/customer'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useCustomerStore } from '@entities/customer'
import { useListsStore } from '@shared/model'
import { resolveUrl } from '@shared/lib/url'
import { useDayjs } from '@shared/lib/format'
import { AppRoute } from '@shared/config'

/** Comments without an agent were written by the system (billing, failed payments, tutor changes…). */
const SYSTEM_AGENT = '(System)'
const REACHED = 'REACHED'

interface UseCustomerStageCommentOptions {
  comment: ComputedRef<CustomerStageComment>
}

interface CustomerStageCommentReturn {
  refAgentName: ComputedRef<string>
  refAgentUrl: ComputedRef<string | null>
  isContacted: ComputedRef<boolean>
  refChannels: ComputedRef<string[]>
  refReach: ComputedRef<string | null>
  isReached: ComputedRef<boolean>
  refDisciplineLabel: ComputedRef<string | null>
  refDate: ComputedRef<string>
}

export const useCustomerStageComment = ({ comment }: UseCustomerStageCommentOptions): CustomerStageCommentReturn => {
  const customerStore = useCustomerStore()
  const listsStore = useListsStore()
  const dayjs = useDayjs()

  const refAgentName = computed<string>(() => comment.value.agent?.name || SYSTEM_AGENT)
  const refAgentUrl = computed<string | null>(() =>
    (comment.value.agent ? resolveUrl(AppRoute.UserEdit, { id: comment.value.agent.id }) : null))

  /** The legacy row showed "via …" only for a contact that has both channels and an outcome. */
  const isContacted = computed<boolean>(() => comment.value.channel.length > 0 && !!comment.value.reach)
  const refChannels = computed<string[]>(() => {
    const labels = listsStore.getLists?.stage_comments?.channel?.all ?? {}

    return comment.value.channel.map(channel => labels[channel] ?? channel)
  })
  const refReach = computed<string | null>(() => {
    const reach = comment.value.reach
    if (!reach) return null

    return listsStore.getLists?.stage_comments?.reach?.[reach] ?? reach
  })
  const isReached = computed<boolean>(() => comment.value.reach === REACHED)

  const refDisciplineLabel = computed<string | null>(() => {
    const discipline = customerStore.getCustomerKidDisciplines.find(item => item.id === comment.value.discipline_id)
    if (!discipline) return null

    const code = listsStore.getLists?.discipline?.code?.[discipline.code] || discipline.code

    return `Kid: ${discipline.kidName} (${code})`
  })

  const refDate = computed<string>(() => dayjs(comment.value.updated_at).format(DateFormat.KanbanDate))

  return {
    refAgentName,
    refAgentUrl,
    isContacted,
    refChannels,
    refReach,
    isReached,
    refDisciplineLabel,
    refDate,
  }
}
